<script setup lang="ts">
import { CheckCircleIcon, ExclamationCircleIcon, PencilIcon } from '@heroicons/vue/solid'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'
import { asyncComputed, useVModel } from '@vueuse/core'
import {
    isLegalComplianceProfileLocked,
    useLegalAccountDataState,
} from '../../../../utilities/account/useAccountDataLegal'
import { isLegalComplianceProfileCategoryComplete } from '../../../wizard/ESAC/legal/form/forms'
import { ref } from 'vue'
import { useLegalAccountFormState } from '../../../../utilities/account/useAccountFormLegal'
import { getPlatformSettings } from '../../../../utilities/usePlatformSettings'
import { esacLegalComplianceProfileConfig } from './esacComplianceProfile'
import { ecspLegalComplianceProfileConfig } from './ecspComplianceProfile'
import { useOpenCloseForm, useSaveForm } from '../../../../utilities/helpers'
import { i18n } from '../../../../localization'
import { CheckIcon } from '@heroicons/vue/outline'
import BaseButtonSecondary from '../../../base/BaseButtonSecondary.vue'

const { legalProfile: account } = useLegalAccountDataState()
const accountDataStore = useLegalAccountDataState()
const accountFormStore = useLegalAccountFormState()

const props = defineProps<{
    form: any
    pageKey: string
    quickEdit?: boolean
    currentForm?: string
}>()

const currentForm = useVModel(props, 'currentForm')

const emit = defineEmits<{
    (e: 'navigateToForm', form: string): void
}>()

const isValid = ref(false)
setIsValid()

async function setIsValid() {
    if (useLegalAccountDataState().loading) {
        return null
    }
    isValid.value = await isLegalComplianceProfileCategoryComplete(props.form.validationKey, props.form.denyKey)
}

const showsWarning = asyncComputed(() => {
    return props.form.showsWarning?.value === true
}, null)

const buttonTitle = !props.form.buttonTitle ? i18n.global.t('account_settings.edit') : props.form.buttonTitle

const complianceProfile =
    getPlatformSettings().compliance_profile === 'esac'
        ? esacLegalComplianceProfileConfig
        : ecspLegalComplianceProfileConfig

const model =
    getPlatformSettings().compliance_profile === 'esac'
        ? account.compliance_profiles.esac
        : account.compliance_profiles.ecsp

const { closeForm, openForm } = useOpenCloseForm()
const { saveForm } = useSaveForm()

const handleCloseForm = async () => {
    await closeForm(model, currentForm, complianceProfile, accountFormStore)
}
const handleOpenForm = async () => {
    await openForm(model, currentForm, complianceProfile, accountFormStore, emit, props.quickEdit, props.pageKey)
}
const handleSaveForm = () => {
    saveForm(currentForm, complianceProfile, accountFormStore, props.quickEdit, setIsValid)
}
</script>

<template>
    <li class="mb-0 p-2 @container">
        <div>
            <div class="flex items-center p-2 sm:p-4">
                <div
                    class="flex min-w-0 flex-1 flex-col items-center justify-between gap-y-3 supports-cq:@sm:flex-row supports-not-cq:sm:flex-row">
                    <div class="flex items-center">
                        <div class="flex-shrink-0">
                            <div class="relative flex items-center justify-center rounded-full bg-white p-1">
                                <div class="relative">
                                    <Component :is="form.icon" class="h-12 w-12 text-gray-300" />
                                    <CheckCircleIcon
                                        v-if="!form.hideStatus && isValid === true"
                                        class="absolute bottom-[-2px] right-[-2px] h-6 w-6 rounded-full bg-white text-green-500" />
                                    <ExclamationCircleIcon
                                        v-else-if="!form.hideStatus && isValid === false"
                                        class="absolute bottom-[-2px] right-[-2px] h-6 w-6 rounded-full bg-white text-red-400" />
                                </div>
                            </div>
                        </div>
                        <div class="min-w-0 flex-1 px-4">
                            <div>
                                <p class="truncate text-sm font-semibold text-gray-900">{{ form.title }}</p>
                                <p v-if="!form.hideStatus" class="mt-1 text-sm text-gray-500">
                                    <i18n-t keypath="compliance_profile.status" tag="p">
                                        <template #status>
                                            <span>{{
                                                isValid
                                                    ? $t('compliance_profile.complete')
                                                    : $t('compliance_profile.incomplete')
                                            }}</span>
                                        </template>
                                    </i18n-t>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="flex w-full justify-end space-x-2">
                        <template v-if="quickEdit && currentForm === pageKey">
                            <BaseButtonMuted class="w-full md:w-auto" @click="handleCloseForm">
                                {{ $t('dialog.button.cancel') }}
                            </BaseButtonMuted>
                            <BaseButtonSecondary
                                class="w-full md:w-auto"
                                :icon="CheckIcon"
                                :loading="accountDataStore.saving"
                                :disabled="accountDataStore.saving || accountFormStore.hasValidationErrors"
                                @click="handleSaveForm">
                                {{ $t('account.save') }}
                            </BaseButtonSecondary>
                        </template>
                        <BaseButtonMuted
                            v-else
                            class="w-full md:w-auto"
                            :disabled="isLegalComplianceProfileLocked()"
                            :icon="PencilIcon"
                            @click="handleOpenForm">
                            {{ buttonTitle }}
                        </BaseButtonMuted>
                    </div>
                </div>
            </div>
            <div v-if="currentForm === pageKey" class="border-t px-4 pt-3 sm:px-6">
                <component
                    :is="complianceProfile[currentForm].component"
                    v-model="model[complianceProfile[currentForm].validationKey]" />
            </div>
        </div>
    </li>
</template>
